import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  NgZone,
  OutputRefSubscription,
  createComponent,
  inject,
} from '@angular/core';
import { FtCampaignContainerComponent } from './ft-campaign-container.component';

/**
 * WS5 Phase 3 #4 — native replacement for the AngularJS `CampaignContainerService`
 * (account-v2/.../v1-adapters/campaign-container/campaignContainer.service.ts), whose
 * `insertElement()` `$compile`-mounted a `<ft-campaign-container-ng ng-if>` string into
 * `document.body` (the MOUNT of the already-native `FtCampaignContainerComponent` was
 * itself `$compile`-driven, forcing AngularJS to keep booting). Mounts the component the
 * same way `NativeTouchpointContainerService` (#3) / `StoryModalLauncherService` do —
 * `createComponent` + `appRef.attachView` + `document.body.appendChild` — with the same
 * `showExisting(propertyUuid, campaignUuid, layerName)` / `createNew(propertyUuid,
 * layerName)` / `hide()` API the journeys + audiences components call.
 *
 * Unlike the touchpoint trigger (#3), the campaign-container consumers (JourneysComponent,
 * AudiencesComponent) are feature components — NOT barrel-declared — so they `inject()`
 * this service directly (no leaf-token indirection needed; they evaluate after the
 * account-shared barrel).
 *
 * PARITY INVARIANT (matched to v1 + the AngularJS service): a single instance is kept open;
 * `showExisting`/`createNew` while already open RE-BIND the four inputs via `ref.setInput()`
 * WITHOUT recreating — exactly as v1's methods reassigned `$ftCampaignContainer` (the
 * `ng-if` stayed truthy → no remount). That input change fires the component's `ngOnChanges`
 * → `initialize()` (reload the header / re-pick the native-vs-v1 branch), unchanged. `hide()`
 * destroys the instance (v1's `hide()` nulled the scope var → `ng-if` false → `$destroy`).
 * Creation (`isCreating`) still routes the component's internal v1-fallback goal editor —
 * deferred to increment #8 — this increment only nativizes the MOUNT.
 *
 * The component signals close via its `close` output (the `.ft-btn-close` button + the
 * campaignCreateCancelCallback) — we subscribe and tear down, so the component needs no
 * back-reference to this service.
 */
@Injectable({ providedIn: 'root' })
export class NativeCampaignContainerService {
  private ref: ComponentRef<FtCampaignContainerComponent> | null = null;
  private closeSub: OutputRefSubscription | null = null;

  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly zone = inject(NgZone);

  showExisting(propertyUuid: string, campaignUuid: string, layerName: string): void {
    this.showOrRebind(propertyUuid, campaignUuid, layerName, false);
  }

  createNew(propertyUuid: string, layerName: string): void {
    this.showOrRebind(propertyUuid, undefined, layerName, true);
  }

  hide(): void {
    if (!this.ref) return;
    const ref = this.ref;
    this.ref = null;
    this.zone.run(() => {
      this.closeSub?.unsubscribe();
      this.closeSub = null;
      this.appRef.detachView(ref.hostView);
      ref.destroy();
      ref.location.nativeElement.remove();
    });
  }

  private showOrRebind(
    propertyUuid: string,
    campaignUuid: string | undefined,
    layerName: string,
    isCreating: boolean,
  ): void {
    this.zone.run(() => {
      if (!this.ref) {
        const ref = createComponent(FtCampaignContainerComponent, {
          environmentInjector: this.environmentInjector,
        });
        ref.setInput('propertyUuid', propertyUuid);
        ref.setInput('campaignUuid', campaignUuid);
        ref.setInput('layerName', layerName);
        ref.setInput('isCreating', isCreating);
        this.closeSub = ref.instance.close.subscribe(() => this.hide());
        this.appRef.attachView(ref.hostView);
        document.body.appendChild(ref.location.nativeElement);
        ref.changeDetectorRef.detectChanges();
        this.ref = ref;
      } else {
        // Re-bind onto the open modal (no remount) — see PARITY INVARIANT above.
        this.ref.setInput('propertyUuid', propertyUuid);
        this.ref.setInput('campaignUuid', campaignUuid);
        this.ref.setInput('layerName', layerName);
        this.ref.setInput('isCreating', isCreating);
        this.ref.changeDetectorRef.detectChanges();
      }
    });
  }
}
